import { format } from "d3-format";
import { tuple } from "fp-ts/lib/function";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { ChartGrid, StackedBar } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_learning_fig_LITPROF";
import { datasets } from "../data/data_learning_fig_LITPROF_datasets";
import { mkGemDataDecoder, useNamedCountries } from "../domain";
import { useMedia } from "../hooks";
import { insertionOrderSet, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, identity, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_learning_fig_LITPROF";
const Survey = io.keyof({
    "STEP (urban areas)": null,
    PIAAC: null
});
const surveyLookup = {
    "STEP (urban areas)": defineMessage({
        id: "fig.learning.LITPROF.STEP",
        defaultMessage: "Standardized Test For English Proficiency"
    }),
    PIAAC: defineMessage({
        id: "fig.learning.LITPROF.PIAAC",
        defaultMessage: "The Survey of Adult Skills"
    })
};
const TargetLevel = io.keyof({
    "Below level 1": null,
    "Level 1": null,
    "Level 2": null,
    "Level 3 and above": null
});
const targetLevelLookup = {
    "Below level 1": defineMessage({
        id: "fig.learning.LITPROF.LevelBelow1",
        defaultMessage: "Below level 1"
    }),
    "Level 1": defineMessage({
        id: "fig.learning.LITPROF.Level1",
        defaultMessage: "Level 1"
    }),
    "Level 2": defineMessage({
        id: "fig.learning.LITPROF.Level2",
        defaultMessage: "Level 2"
    }),
    "Level 3 and above": defineMessage({
        id: "fig.learning.LITPROF.Level3",
        defaultMessage: "Level 3 and above"
    })
};
export const Data = mkGemDataDecoder([
    "iso3c",
    "ind_id"
], {
    value: io.number,
    level: TargetLevel,
    survey: Survey
});
export const Chart = ({ data: rawData })=>{
    const intl = useIntl();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const data_ = useNamedCountries(rawData);
    const data = React.useMemo(()=>{
        return data_.map((x)=>({
                ...x,
                survey_name: intl.formatMessage(surveyLookup[x.survey]),
                level_name: intl.formatMessage(targetLevelLookup[x.level])
            }));
    }, [
        intl,
        data_
    ]);
    const chartData = React.useMemo(()=>{
        return pipe(data, Ar.sort(Ord.contramap((x)=>tuple(x.survey === "PIAAC" ? -1 : 1, x.level === "Below level 1" ? -x.value : Infinity, x.level === "Level 1" ? -x.value : Infinity, x.level === "Level 2" ? -x.value : Infinity, x.level === "Level 3 and above" ? -x.value : Infinity))(Ord.getTupleOrd(Ord.ordNumber, Ord.ordNumber, Ord.ordNumber, Ord.ordNumber, Ord.ordNumber))));
    }, [
        data
    ]);
    const getColumn = React.useCallback((x)=>x.survey_name, []);
    const getStack = React.useCallback((x)=>x.survey === "PIAAC" ? x.country_name : `${x.country_name}, ${intl.formatMessage(defineMessage({
            id: "urban",
            defaultMessage: "urban"
        }))}`, [
        intl
    ]);
    const getLayer = React.useCallback((x)=>x.level_name, []);
    const getValue = React.useCallback((x)=>x.value || 0, []);
    const formatY = React.useMemo(()=>format(".0%"), []);
    const getColorPalette = React.useMemo(()=>{
        const stati = insertionOrderSet(unsafeFromArray(data), (x)=>x.level_name);
        return ()=>M.fromCount(M.colorRanges.diverging, stati.length);
    }, [
        data
    ]);
    return pipe(An.fromArray(chartData), O.map((xs)=><ChartGrid key={`col-${getColumn(An.head(xs))}`} data={xs} getCell={getColumn} columnCount={1} columnFractions={screenSDown ? [
            2,
            1
        ] : [
            4,
            1
        ]} minWidth={0}>
        {({ data, width, first })=><StackedBar key={"linter-map"} width={width} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={data} getStack={getStack} getLayer={getLayer} getLayerLabel={identity} getValue={getValue} formatX={identity} formatY={formatY} colorPalette={getColorPalette} showTooltip hideXAxisLabel hideYAxis={!first} hideLegend={!first}/>}
      </ChartGrid>), O.toNullable);
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_LITPROF.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_LITPROF.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_LITPROF.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
